import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChatBubbleLeftRightIcon, 
  PlusIcon, 
  PlayIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  const features = [
    {
      name: 'Interactive Cases',
      description: 'Practice with realistic medical scenarios and difficult conversations.',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      name: 'AI-Powered Feedback',
      description: 'Get instant feedback on your communication skills using advanced AI.',
      icon: AcademicCapIcon,
    },
    {
      name: 'Audio Generation',
      description: 'Listen to realistic conversations with text-to-speech technology.',
      icon: PlayIcon,
    },
    {
      name: 'Collaborative Learning',
      description: 'Share and discuss cases with instructors and peers.',
      icon: UserGroupIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Difficult Conversations
              <span className="block text-blue-200">Simulator</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Master the art of difficult medical conversations through interactive scenarios, 
              AI-powered feedback, and realistic practice sessions.
            </p>
            {isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/cases"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
                >
                  <PlayIcon className="h-5 w-5 mr-2" />
                  Start Practicing
                </Link>
                <Link
                  to="/cases/new"
                  className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create New Case
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
                >
                  Get Started
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Our Platform?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive platform provides everything you need to develop 
              exceptional communication skills for difficult medical conversations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.name} className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white mx-auto mb-4">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.name}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Institutional Support section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Supported by Leading Institutions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This platform is developed through a collaboration between McGill University and Linarite AI, 
              bringing together academic excellence and cutting-edge AI technology.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* McGill University */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-6">
                {/* McGill Logo Placeholder - Replace with official logo */}
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-600 mr-4 relative">
                  <BuildingOfficeIcon className="h-8 w-8" />
                  {/* Uncomment and replace with actual McGill logo */}
                  {/* <img 
                    src="/logos/mcgill-logo.png" 
                    alt="McGill University Logo" 
                    className="h-12 w-12 object-contain"
                  /> */}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">McGill University</h3>
                  <p className="text-gray-600">Montreal, Canada</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                A leading research university known for excellence in medical education and healthcare innovation. 
                McGill's Faculty of Medicine is at the forefront of medical training and research.
              </p>
              <div className="flex items-center justify-center md:justify-start text-sm text-gray-500">
                <HeartIcon className="h-4 w-4 mr-1" />
                <span>Established 1821 • World-class medical education</span>
              </div>
            </div>

            {/* Linarite AI */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-6">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mr-4">
                  <AcademicCapIcon className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Linarite AI</h3>
                  <p className="text-gray-600">AI Technology Partner</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Specialized in developing AI-powered educational tools and healthcare simulation technologies. 
                Committed to advancing medical education through innovative AI solutions.
              </p>
              <div className="flex items-center justify-center md:justify-start text-sm text-gray-500">
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                <span>AI-powered • Healthcare simulation • Educational technology</span>
              </div>
            </div>
          </div>

          {/* Collaboration highlight */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-blue-50 to-red-50 border border-blue-200">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-red-600 rounded-full"></div>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                Advancing medical communication training through academic excellence and AI innovation
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
              <div className="text-lg text-gray-600">Medical Cases</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-lg text-gray-600">Institutions</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">10K+</div>
              <div className="text-lg text-gray-600">Students Trained</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA section */}
      {!isAuthenticated && (
        <div className="py-16 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Improve Your Communication Skills?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of medical professionals who have enhanced their 
              difficult conversation skills with our platform.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
            >
              Start Your Free Trial
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
